import Image from '@atlaskit/image';
import type { SmartPrompt } from '@atlassian/conversation-assistant-blank-object-experience/types';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl-next';

import { fetchWithMemoryCache } from '@canvas/connection';
import { captureException } from '@canvas/sentry-react';

import {
  DATABASE_SMART_PROMPT_ENTRIES,
  type DatabaseSmartPromptEntry,
} from './database-smart-prompt-entries';

// Mirrors the job-title-to-role mapping in createWithRovoSmartPrompts.tsx
// Source: confluence/next/packages/blank-page-quick-actions/src/createWithRovoSmartPrompts.tsx
const keywordToCategoryMap: ReadonlyArray<readonly [string, string]> = [
  ['engineer', 'engineering'],
  ['developer', 'engineering'],
  ['programmer', 'engineering'],
  ['product', 'productManagement'],
  ['pm', 'productManagement'],
  ['marketing', 'marketing'],
  ['brand', 'marketing'],
  ['campaign', 'marketing'],
  ['sales', 'sales'],
  ['account', 'sales'],
  ['program', 'programManagement'],
  ['project manager', 'programManagement'],
];

const getJobCategoryForTitle = (jobTitle: string | undefined): string => {
  if (!jobTitle) {
    return 'other';
  }
  const lowerTitle = jobTitle.toLowerCase();
  for (const [keyword, category] of keywordToCategoryMap) {
    if (lowerTitle.includes(keyword)) {
      return category;
    }
  }
  return 'other';
};

const createIconComponent = (iconUrl: string): SmartPrompt['icon'] => {
  const SmartPromptIcon = ({ size }: { size?: string; color?: string } = {}) => {
    const dimension = size === 'small' ? 20 : 100;
    return React.createElement(Image, {
      src: iconUrl,
      alt: '',
      width: dimension,
      height: dimension,
    });
  };
  SmartPromptIcon.displayName = 'SmartPromptIcon';
  return SmartPromptIcon;
};

const toSmartPrompt = (
  entry: DatabaseSmartPromptEntry,
  formatMessage: IntlShape['formatMessage']
): SmartPrompt => ({
  name: entry.name,
  text: entry.prompt,
  label: formatMessage(entry.templateName),
  icon: createIconComponent(entry.icon),
  templateSnapshot: { default: '', dark: '' },
  prompt: entry.prompt,
  templateName: entry.templateName,
});

const getDatabaseSmartPrompts = (
  jobTitle: string | undefined,
  formatMessage: IntlShape['formatMessage']
): SmartPrompt[] => {
  const jobCategory = getJobCategoryForTitle(jobTitle);

  const primaryEntries = DATABASE_SMART_PROMPT_ENTRIES.filter(
    (e) => e.userRole === jobCategory && e.cardLevel === 'primary'
  );

  const secondaryEntries = DATABASE_SMART_PROMPT_ENTRIES.filter(
    (e) =>
      (e.userRole === jobCategory && e.cardLevel === 'secondary') ||
      (e.userRole === 'other' && (jobCategory !== 'other' || e.cardLevel !== 'primary'))
  );

  return [...primaryEntries, ...secondaryEntries]
    .slice(0, 8)
    .map((entry) => toSmartPrompt(entry, formatMessage));
};

export interface UseDatabaseSmartPromptsResult {
  smartPrompts: SmartPrompt[];
  isLoading: boolean;
  hasError: boolean;
}

/**
 * Fetches the current user's job title from the Confluence REST API and returns
 * smart prompts filtered by their role. Returns an empty array while loading to
 * avoid a flash of generic "other" prompts before the job title resolves.
 * On error, falls back to the generic "other" prompts so the modal is still useful.
 */
export const useDatabaseSmartPrompts = (): UseDatabaseSmartPromptsResult => {
  const { formatMessage } = useIntl();
  const [jobTitle, setJobTitle] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchJobTitle = async () => {
      try {
        const response = await fetchWithMemoryCache(
          '/gateway/api/me',
          {},
          { cacheKey: 'identity' }
        );
        if (!response.ok) {
          if (!cancelled) {
            setHasError(true);
            setIsLoading(false);
          }
          return;
        }
        const data = await response.json();
        if (!cancelled) {
          setJobTitle(data?.extended_profile?.job_title || undefined);
          setIsLoading(false);
        }
      } catch (e) {
        captureException(e, { tags: { captureLocation: 'useDatabaseSmartPrompts' } });
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    fetchJobTitle();
    return () => {
      cancelled = true;
    };
  }, []);

  // When loading, return empty prompts to avoid a flash of "other" prompts.
  // On error, jobTitle remains undefined, which getJobCategoryForTitle maps to
  // 'other', so we naturally fall back to the generic prompts.
  const smartPrompts = useMemo(
    () => (isLoading ? [] : getDatabaseSmartPrompts(jobTitle, formatMessage)),
    [isLoading, jobTitle, formatMessage]
  );

  return { smartPrompts, isLoading, hasError };
};
